/**
 * Product copy + catalogs.
 *
 *   t('auth.login.signIn')
 *   t('catalogs.competencies.10.title')
 *   t('dashboard.welcome', { name: 'Ada' })
 *   get('help.items')
 *
 * Locale files export `{ en, de, fi }` with the same key tree.
 * Do not use English sentences as keys. Components import from this file,
 * not from catalogs/ or features/ directly.
 *
 * t()/get() follow the active UI locale (synced from LocaleProvider).
 * Missing DE values fall back to English at read time — do not copy English
 * into DE files. At runtime LocaleProvider fetches Contentful and overlays
 * these local dictionaries. If env is missing or CDA fails, t()/lookups
 * keep using this local snapshot.
 */
import { DEFAULT_LOCALE, LOCALES } from '../locale/constants';
import { getActiveLocale } from './runtime';
import { dictionaries, localDictionaries } from './store';

import competencies from './catalogs/competencies';
import motivations from './catalogs/motivations';
import personality from './catalogs/personality';
import teamStrengths from './catalogs/teamStrengths';
import salesStrengths from './catalogs/salesStrengths';
import adept from './catalogs/adept';
import assessments from './catalogs/assessments';
import reports from './catalogs/reports';

import common from './ui/common';
import validation from './ui/validation';
import toasts from './ui/toasts';
import emptyStates from './ui/emptyStates';
import errors from './ui/errors';

import nav from './features/nav';
import auth from './features/auth';
import dashboard from './features/dashboard';
import talents from './features/talents';
import help from './features/help';
import account from './features/account';
import assessmentsUi from './features/assessments';
import dataShare from './features/dataShare';

function pick(mod, locale) {
  return mod[locale] || mod.en;
}

function walk(dict, path) {
  return path.split('.').reduce(
    (node, key) => (node == null ? undefined : node[key]),
    dict
  );
}

function buildDictionary(locale = DEFAULT_LOCALE) {
  return {
    catalogs: {
      competencies: pick(competencies, locale),
      motivations: pick(motivations, locale),
      personality: pick(personality, locale),
      teamStrengths: pick(teamStrengths, locale),
      salesStrengths: pick(salesStrengths, locale),
      adept: pick(adept, locale),
      assessments: pick(assessments, locale),
      reports: pick(reports, locale),
    },
    common: pick(common, locale),
    validation: pick(validation, locale),
    toasts: pick(toasts, locale),
    emptyStates: pick(emptyStates, locale),
    errors: pick(errors, locale),
    nav: pick(nav, locale),
    auth: pick(auth, locale),
    dashboard: pick(dashboard, locale),
    talents: pick(talents, locale),
    help: pick(help, locale),
    account: pick(account, locale),
    assessments: pick(assessmentsUi, locale),
    dataShare: pick(dataShare, locale),
  };
}

LOCALES.forEach((locale) => {
  const dict = buildDictionary(locale);
  localDictionaries[locale] = dict;
  dictionaries[locale] = JSON.parse(JSON.stringify(dict));
});

export function get(path, locale = getActiveLocale()) {
  const dict = dictionaries[locale] || dictionaries[DEFAULT_LOCALE];
  const value = walk(dict, path);
  if (value !== undefined) return value;
  if (locale !== DEFAULT_LOCALE) {
    return walk(dictionaries[DEFAULT_LOCALE], path);
  }
  return undefined;
}

export function t(path, vars = {}, locale = getActiveLocale()) {
  const value = get(path, locale);
  if (typeof value !== 'string') return path;
  return value.replace(/\{(\w+)\}/g, (_, name) =>
    vars[name] == null ? `{${name}}` : String(vars[name])
  );
}

export { LOCALES, DEFAULT_LOCALE };
export { getActiveLocale, setActiveLocale } from './runtime';
export { dictionaries } from './store';
export { lookups, buildLookups, getLookups, clearLookupsCache } from './lookups';
